import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Activity, RefreshCw, Send, Users, Zap } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { apiPost, operationsClient } from '@/lib/api';
import { leadKeys } from '@/lib/queryKeys';
import { useLeads } from '@/hooks/leads/useLeads';
import { useLeadSettings } from '@/hooks/leads/useLeadSettings';
import { useLeadOwners } from '@/hooks/leads/useLeadOwners';
import { AssignmentMethodBadge } from '@/components/organisms/leads/AssignmentMethodBadge';
import { SlaCountdown } from '@/components/organisms/leads/SlaCountdown';
import { formatUserName } from '@/lib/leads/leadFormat';
import type { Lead } from '@/types/lead';

const INGEST_SOURCES: { path: string; label: string }[] = [
  { path: 'generic', label: 'Website form' },
  { path: 'chat', label: 'Chat' },
  { path: 'phone-call', label: 'Inbound call' },
  { path: 'angi', label: 'Angi' },
  { path: 'yelp', label: 'Yelp' },
  { path: 'thumbtack', label: 'Thumbtack' },
  { path: 'google-lsa', label: 'Google LSA' },
  { path: 'facebook', label: 'Facebook' },
  { path: 'email', label: 'Email' },
];

interface IngestLead {
  id: string;
  leadNumber?: string;
  source?: string;
  assignedTo?: string | null;
  assignedBy?: Lead['assignedBy'];
}

// POST /leads/ingest/:source returns an envelope, not a flat lead:
// `{ lead: <enriched lead>, deduplicated: boolean }`. Read fields off `lead`.
interface IngestResponse {
  lead: IngestLead;
  deduplicated: boolean;
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Zap;
  label: string;
  value: string;
  tone?: 'green' | 'amber' | 'muted';
}) {
  const toneClass =
    tone === 'green' ? 'text-emerald-600' : tone === 'amber' ? 'text-amber-600' : 'text-foreground';
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </span>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`truncate text-sm font-semibold ${toneClass}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function LeadTestIntakePanel({ canCreateLead }: { canCreateLead: boolean }) {
  const qc = useQueryClient();
  const settingsQuery = useLeadSettings();
  const usersQuery = useLeadOwners({ enabled: true });
  const leadsQuery = useLeads({ limit: 100 });

  const settings = settingsQuery.data;
  const users = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);
  const userName = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of users) map.set(user.id, formatUserName(user));
    return map;
  }, [users]);

  const leads = useMemo(() => {
    const list = leadsQuery.data?.data ?? [];
    return [...list].sort((a, b) => (b.leadNumber ?? '').localeCompare(a.leadNumber ?? ''));
  }, [leadsQuery.data]);

  const workload = useMemo(() => {
    const open = leads.filter((lead) => !lead.stage?.isWon && !lead.stage?.isLost);
    const counts = new Map<string, number>();
    for (const lead of open) {
      if (!lead.assignedTo) continue;
      counts.set(lead.assignedTo, (counts.get(lead.assignedTo) ?? 0) + 1);
    }
    return users
      .map((user) => ({ user, count: counts.get(user.id) ?? 0 }))
      .sort((a, b) => b.count - a.count);
  }, [leads, users]);

  const routeCount =
    (settings?.categoryRoutes?.length ?? 0) +
    (settings?.territoryRoutes?.length ?? 0) +
    (settings?.sourceRoutes?.length ?? 0);

  const [source, setSource] = useState('generic');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<IngestResponse | null>(null);

  async function sendLead() {
    if (!canCreateLead) {
      toast.error('You do not have permission to create leads');
      return;
    }
    if (!name.trim() || !phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    setSending(true);
    try {
      const res = await apiPost<IngestResponse>(operationsClient, `/leads/ingest/${source}`, {
        name,
        phone,
        service,
        message: service,
      });
      setLastResult(res);
      toast.success(`Lead ${res.lead.leadNumber ?? ''} created`);
      setName('');
      setPhone('');
      setService('');
      await qc.invalidateQueries({ queryKey: leadKeys.all() });
    } catch {
      toast.error('Could not create the lead');
    } finally {
      setSending(false);
    }
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: leadKeys.all() });
  }

  const ownerLabel = (lead: Lead) =>
    lead.assignedTo ? (userName.get(lead.assignedTo) ?? 'Assigned') : 'Unassigned';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Create a test lead, watch routing assign it, and confirm who owns it. Configure stages,
          SLA, and routing in the <span className="font-medium">Configuration</span> tab.
        </p>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Zap}
          label="Auto-assign"
          value={settings?.autoAssignEnabled ? 'Enabled' : 'Off'}
          tone={settings?.autoAssignEnabled ? 'green' : 'amber'}
        />
        <StatCard
          icon={Users}
          label="Fallback owner"
          value={
            settings?.fallbackUserId ? (userName.get(settings.fallbackUserId) ?? 'Set') : 'None'
          }
          tone={settings?.fallbackUserId ? 'muted' : 'amber'}
        />
        <StatCard icon={Activity} label="Routing rules" value={`${routeCount}`} />
        <StatCard
          icon={Users}
          label="Rep capacity"
          value={`${settings?.repCapacityLimit ?? 20} open leads`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Send className="h-4 w-4 text-muted-foreground" />
              Test intake
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Simulate an inbound source. This creates a real lead and runs routing immediately.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INGEST_SOURCES.map((item) => (
                    <SelectItem key={item.path} value={item.path}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Customer name</Label>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Dana Rivera"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="+1 555 123 4567"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Service requested</Label>
              <Input
                value={service}
                onChange={(event) => setService(event.target.value)}
                placeholder="AC not cooling"
              />
            </div>
            <Button className="w-full" onClick={sendLead} disabled={sending || !canCreateLead}>
              <Send className="mr-1.5 h-4 w-4" />
              {sending ? 'Sending...' : 'Send lead'}
            </Button>
            {!canCreateLead ? (
              <p className="text-xs text-muted-foreground">
                You need lead creation permission to send test leads.
              </p>
            ) : null}

            {lastResult ? (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <p className="font-medium">{lastResult.lead.leadNumber} created</p>
                <p className="flex flex-wrap items-center gap-1 text-muted-foreground">
                  Source {lastResult.lead.source} - assigned to{' '}
                  <span className="font-medium text-foreground">
                    {lastResult.lead.assignedTo
                      ? (userName.get(lastResult.lead.assignedTo) ?? 'a rep')
                      : 'no one (unassigned)'}
                  </span>
                  <AssignmentMethodBadge method={lastResult.lead.assignedBy} />
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-muted-foreground" />
              Owners &amp; workload
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Open leads currently assigned to each person.
            </p>
          </CardHeader>
          <CardContent>
            {workload.length === 0 ? (
              <p className="text-sm text-muted-foreground">No assignable users.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {workload.map(({ user, count }) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <span className="truncate text-sm">{formatUserName(user)}</span>
                    <Badge variant={count > 0 ? 'default' : 'outline'}>{count} open</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Recent routed leads
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Newest first - who owns each lead and how routing assigned it.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Assigned via</TableHead>
                  <TableHead>SLA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No leads yet - send a test lead from the panel above.
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.slice(0, 40).map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-medium">
                        <Link
                          to="/dashboard/leads/$leadId"
                          params={{ leadId: lead.id }}
                          search={{ tab: 'overview' }}
                          className="hover:underline"
                        >
                          {lead.leadNumber}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{lead.source}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate">
                        {lead.contact?.displayName ||
                          [lead.contact?.firstName, lead.contact?.lastName]
                            .filter(Boolean)
                            .join(' ') ||
                          '-'}
                      </TableCell>
                      <TableCell>
                        <span
                          className="inline-block rounded px-2 py-0.5 text-xs"
                          style={{
                            backgroundColor: `${lead.stage?.color ?? '#e5e7eb'}22`,
                            color: lead.stage?.color ?? undefined,
                          }}
                        >
                          {lead.stage?.name ?? '-'}
                        </span>
                      </TableCell>
                      <TableCell className={lead.assignedTo ? '' : 'text-amber-600'}>
                        {ownerLabel(lead)}
                      </TableCell>
                      <TableCell>
                        <AssignmentMethodBadge method={lead.assignedBy} />
                      </TableCell>
                      <TableCell>
                        <SlaCountdown lead={lead} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
